import {stringify} from 'qs';
import {createSelector} from 'reselect';

import {Controller} from '@stubhub/react-store-provider';
import {get} from '@stubhub/rest-method';

import {SEARCH_CACHE_MAX_AGE} from '../../utils/constants';

const NAMESPACE = 'RELATED_ARTISTS';

// Selectors
const deviceTypeSelector = createSelector(
  (state = {}) => state.device,
  (device = '') => device.type
);
const screenSizeSelector = createSelector(
  (state = {}) => state.viewport,
  (viewport = {}) => viewport.screenSize
);
const mobileViewSelector = createSelector(
  [deviceTypeSelector, screenSizeSelector],
  (deviceType = '', screenSize = '') =>
    (!screenSize && deviceType === 'phone') ||
    screenSize === 'small' ||
    (screenSize === 'medium' && deviceType === 'phone')
);
const userStateSelector = (state) => state.user;
const defaultGeoLocation = {
  latitude: 37.77493,
  longitude: -122.41942,
};
const userLocationSelector = createSelector(userStateSelector, (user) =>
  user ? user.location || defaultGeoLocation : defaultGeoLocation
);

const ACTION_TYPES = {
  RELATED_ARTISTS_LOADING: 'RELATED_ARTISTS_LOADING',
  RELATED_ARTISTS_LOADED: 'RELATED_ARTISTS_LOADED',
  RELATED_ARTISTS_RESET: 'RELATED_ARTISTS_RESET',
};

const MIN_RELATED_ARTISTS = 10;

const loadPerformers = (props) => {
  return (dispatch, getState) => {
    const state = getState();
    const lang = state?.lang;
    const shstoreId = state && state.gsConfig && state.gsConfig.shstoreId;
    dispatch({type: ACTION_TYPES.RELATED_ARTISTS_LOADING, isLoading: true, performers: []});

    const baseOptions = {
      start: 0,
      row: 10,
      shstoreId,
      status: 'active',
    };

    const {url, options = {}, dataHandler} = getUrlOptions(props);
    if (url) {
      return sendRequest(url, {...baseOptions, ...options}, {lang}).then((body) => {
        const {data, numFound} = dataHandler(body);
        dispatch({
          type: ACTION_TYPES.RELATED_ARTISTS_LOADED,
          isLoading: false,
          performers: data,
          total: Math.min(numFound, MIN_RELATED_ARTISTS),
        });

        return body;
      });
    }
  };
};

const getUrlOptions = (props) => {
  if (props.isSport) {
    return getSportsUrl(props);
  }

  return getConcertsUrl(props);
};

/**
 * Get concerts related artists api
 * @param {*} props
 */
const getConcertsUrl = (props) => {
  const {latitude, longitude} = props.geo || {};
  const {entityId} = props;
  const url = '/search/catalog/events/v3/eventGroupings';
  const options = {
    sort: 'popularity desc,distance asc',
    performerId: `-${entityId}`,
    ir: true,
    parking: false,
    geoExpansion: true,
    minAvailableTickets: 1,
    nearestFirstAfterGeoExpansion: true,
    point: `${latitude},${longitude}`,
    radius: 50,
    categoryId: getConcertCategoriesFilter(props.categories),
  };

  const dataHandler = (body = {}) => {
    const {groups = [], numFound = 0} = body;

    return {
      data: groups,
      numFound,
    };
  };

  return {
    url,
    options,
    dataHandler,
  };
};

/**
 * Choose valid categories among all category ancestors (only for concerts)
 * Priorities:
 * 1. category with type "PRIMARY_GENRE" or "SECONDARY_GENRE" or "PRIMARY_STYLE"
 * 2. if #1 does not exist, combine all with type "SECONDARY_STYLE"
 * 3. if #1 and #2 do not exist, combine all categories
 * @param {*} categories
 */
const getConcertCategoriesFilter = (categories = []) => {
  const categoryIds = [];
  categories.forEach((item) => {
    (item.relationshipType === 'PRIMARY_GENRE' ||
      item.relationshipType === 'SECONDARY_GENRE' ||
      item.relationshipType === 'PRIMARY_STYLE') &&
      categoryIds.push(item.id);
  });

  if (categoryIds.length === 0) {
    categories.forEach((item) => {
      item.relationshipType === 'SECONDARY_STYLE' && categoryIds.push(item.id);
    });

    if (categoryIds.length === 0) {
      categories.forEach((item) => {
        categoryIds.push(item.id);
      });
    }
  }

  return categoryIds.join(' |');
};

/**
 * Get sports related artists api
 * If current performer has groupings info, use groupings as filter params
 * If not, use categories as filter params
 * @param {*} props
 */
const getSportsUrl = (props) => {
  let url = '/search/catalog/performers/v3';
  const options = {
    sort: 'popularity desc',
  };
  let dataHandler;

  const {categories = [], groupings = [], entityId} = props;
  if (groupings.length > 0) {
    options.ir = true;
    options.id = `-${entityId}`;
    // Get first element of groupings array
    options.groupingId = groupings[0].id;

    dataHandler = (body = {}) => {
      const {performers = [], numFound = 0} = body;

      return {
        data: performers,
        numFound,
      };
    };
  } else {
    url = '/search/catalog/events/v3/eventGroupings';
    options.ir = true;
    options.parking = false;
    options.performerId = `-${entityId}`;
    const {latitude, longitude} = props.geo || {};
    options.point = `${latitude},${longitude}`;
    options.radius = 0;
    options.geoExpansion = true;
    options.nearestFirstAfterGeoExpansion = true;
    options.sort = 'distance asc,popularity desc';
    // Get first element of categories array
    options.categoryId = categories?.[0]?.id;

    dataHandler = (body = {}) => {
      const {groups = [], numFound = 0} = body;

      return {
        data: groups,
        numFound,
      };
    };
  }

  return {
    url,
    options,
    dataHandler,
  };
};

const sendRequest = (path, params, {lang} = {}) => {
  const cache = true;
  const cacheMaxAge = SEARCH_CACHE_MAX_AGE;

  return get({
    host: process.env.REACT_APP_API_HOST,
    path: `${path}?${params ? stringify(params) : ''}`,
    headers: {
      Authorization: process.env.REACT_APP_API_SECRET,
      'Accept-Language': lang,
    },
    json: true,
    cache,
    cacheMaxAge,
  });
};

const resetPerformers = () => {
  return (dispatch) => {
    dispatch({type: 'RELATED_ARTISTS_RESET', isLoading: false, performers: [], total: 0});
  };
};

const controller = new Controller({
  namespace: NAMESPACE,
  mapStateToProps(state) {
    const localState = this.getLocalState(state);
    const gsConfig = state.gsConfig || {};

    /* istanbul ignore next */
    return {
      gsConfig,
      geo: userLocationSelector(state),
      isLoading: localState.isLoading !== false,
      performers: localState.performers,
      isMobileView: mobileViewSelector(state),
      notFound: localState.notFound,
      total: localState.total,
      deviceType: deviceTypeSelector(state),
      screenSize: screenSizeSelector(state),
    };
  },
  actionCreators: {
    loadPerformers,
    resetPerformers,
  },
  reducers: [
    ACTION_TYPES.RELATED_ARTISTS_LOADING,
    ACTION_TYPES.RELATED_ARTISTS_LOADED,
    ACTION_TYPES.RELATED_ARTISTS_RESET,
    'USER_LOCATION_CHANGE',
  ],
});

export default controller;
